import React, { useState, useContext, ReactNode } from 'react'
import { TooltipOptions, TooltipProps } from './types'
import { TooltipContext } from './TooltipContext'
import { TooltipContainer } from './TooltipContainer'

export * from './types'
export * from './constants'
export { Tooltip } from './Tooltip'

interface TooltipFn {
  showTooltip: (content: ReactNode) => void
  unload: () => void
}

interface TooltipProviderProps {
  children: ReactNode
}

/**
 * Used to render a tooltip with complete control over the content and options.
 * @param triggerRef
 * @param options see https://storybook-dev.consumer.au.platform.afterpay-beta.com/?path=/docs/component-tooltip--docs
 * @returns
 */
export const useTooltip = (triggerRef: React.RefObject<HTMLElement>, options?: TooltipOptions, ariaIsOpen?: boolean): TooltipFn => {
  const { tooltipList, addTooltip, removeTooltip } = useContext(TooltipContext)

  const showTooltip = (content: ReactNode): void => {
    const tooltip = tooltipList.find((item) => item.triggerRef === triggerRef)
    if (typeof tooltip !== 'undefined') {
      return
    }

    const id = Math.random().toString(36)
    addTooltip({ ...options, content, triggerRef, id, ariaIsOpen })
  }

  const unload = (): void => {
    const tooltip = tooltipList.find((item) => item.triggerRef === triggerRef)
    typeof tooltip !== 'undefined' && removeTooltip(tooltip.id)
  }

  return {
    showTooltip,
    unload,
  }
}

export const TooltipProvider = ({ children }: TooltipProviderProps): JSX.Element => {
  const [tooltipList, setTooltipList] = useState<TooltipProps[]>([])

  const addTooltip = (props: TooltipProps): void => {
    setTooltipList([...tooltipList, props])
  }

  const removeTooltip = (id?: string): void => {
    setTooltipList(tooltipList.filter((item) => item.id !== id))
  }

  return (
    <TooltipContext.Provider value={{ tooltipList, addTooltip, removeTooltip }}>
      {children}
      <TooltipContainer />
    </TooltipContext.Provider>
  )
}
